import { apiFetchData } from '@/shared/lib/api';
import type {
  MenuItemResponse,
  CategoryResponse,
  AddOnResponse,
  AddOnCategoryResponse,
} from '@/features/menu/types';
import type { RestaurantResponse } from '@/features/restaurants/types';
import type { DealSectionResponse } from './types/deals';
import {
  mapCategoryFromBackend,
  mapMenuItemFromBackend,
  mapAddOnFromBackend,
  mapAddOnCategoryFromBackend,
  mapDealFromBackend,
  mapRestaurantFromBackend,
} from './mappers';

/**
 * Public API client — customer-facing endpoints that don't require authentication.
 */
export const publicApi = {
  // ==================== PUBLIC RESTAURANT INFO ====================

  async getAllRestaurants(): Promise<RestaurantResponse[]> {
    const restaurants = await apiFetchData<Array<Record<string, unknown>>>(
      `/api/public/restaurants`,
      { method: 'GET' }
    );
    return (restaurants ?? []).map(mapRestaurantFromBackend);
  },

  async getRestaurantById(restaurantId: string): Promise<RestaurantResponse> {
    const raw = await apiFetchData<Record<string, unknown>>(
      `/api/public/restaurants/${restaurantId}`,
      { method: 'GET' }
    );
    return mapRestaurantFromBackend(raw);
  },

  async getRestaurantBySlug(slug: string): Promise<RestaurantResponse> {
    const raw = await apiFetchData<Record<string, unknown>>(
      `/api/public/restaurants/slug/${slug}`,
      { method: 'GET' }
    );
    return mapRestaurantFromBackend(raw);
  },

  async getRestaurantBySubdomain(subdomain: string): Promise<RestaurantResponse> {
    const raw = await apiFetchData<Record<string, unknown>>(
      `/api/public/restaurants/subdomain/${subdomain}`,
      { method: 'GET' }
    );
    return mapRestaurantFromBackend(raw);
  },

  async getRestaurantByCustomDomain(customDomain: string): Promise<RestaurantResponse> {
    const raw = await apiFetchData<Record<string, unknown>>(
      `/api/public/restaurants/domain/${customDomain}`,
      { method: 'GET' }
    );
    return mapRestaurantFromBackend(raw);
  },

  // ==================== PUBLIC MENU ====================

  async getFullMenu(restaurantId: string): Promise<{
    restaurantId: string;
    itemsByCategory: Record<string, MenuItemResponse[]>;
    addOnsByCategory: Record<string, AddOnResponse[]>;
    categories: CategoryResponse[];
  }> {
    const raw = await apiFetchData<{
      restaurantId?: string;
      itemsByCategory?: Record<string, Array<Record<string, unknown>>>;
      addOnsByCategory?: Record<string, Array<Record<string, unknown>>>;
      categories?: Array<Record<string, unknown>>;
    }>(`/api/public/restaurants/${restaurantId}/menu`, { method: 'GET' });
    const categories = (raw?.categories ?? []).map(mapCategoryFromBackend);
    const itemsByCategory: Record<string, MenuItemResponse[]> = {};
    if (raw?.itemsByCategory && typeof raw.itemsByCategory === 'object') {
      for (const [catName, items] of Object.entries(raw.itemsByCategory)) {
        itemsByCategory[catName] = (items ?? []).map(mapMenuItemFromBackend);
      }
    }
    const addOnsByCategory: Record<string, AddOnResponse[]> = {};
    if (raw?.addOnsByCategory && typeof raw.addOnsByCategory === 'object') {
      for (const [catName, addOns] of Object.entries(raw.addOnsByCategory)) {
        addOnsByCategory[catName] = (addOns ?? []).map(mapAddOnFromBackend);
      }
    }
    return {
      restaurantId: (raw?.restaurantId ?? restaurantId) as string,
      itemsByCategory,
      addOnsByCategory,
      categories,
    };
  },

  async getMenuItems(restaurantId: string): Promise<MenuItemResponse[]> {
    const items = await apiFetchData<Array<Record<string, unknown>>>(
      `/api/public/restaurants/${restaurantId}/menu/items`,
      { method: 'GET' }
    );
    return (items ?? []).map(mapMenuItemFromBackend);
  },

  async getMenuItemById(restaurantId: string, itemId: string): Promise<MenuItemResponse> {
    const item = await apiFetchData<Record<string, unknown>>(
      `/api/public/restaurants/${restaurantId}/menu/items/${itemId}`,
      { method: 'GET' }
    );
    return mapMenuItemFromBackend(item);
  },

  async getMenuItemsByCategory(restaurantId: string, category: string): Promise<MenuItemResponse[]> {
    const items = await apiFetchData<Array<Record<string, unknown>>>(
      `/api/public/restaurants/${restaurantId}/menu/items/category/${category}`,
      { method: 'GET' }
    );
    return (items ?? []).map(mapMenuItemFromBackend);
  },

  async getCategories(restaurantId: string): Promise<CategoryResponse[]> {
    const raw = await apiFetchData<Array<Record<string, unknown>>>(
      `/api/public/restaurants/${restaurantId}/menu/categories`,
      { method: 'GET' }
    );
    return (Array.isArray(raw) ? raw : []).map(mapCategoryFromBackend);
  },

  async getCategoryById(restaurantId: string, categoryId: string): Promise<CategoryResponse> {
    const raw = await apiFetchData<Record<string, unknown>>(
      `/api/public/restaurants/${restaurantId}/menu/categories/${categoryId}`,
      { method: 'GET' }
    );
    return mapCategoryFromBackend(raw ?? {});
  },

  // ==================== PUBLIC ADD-ONS ====================

  async getAddOns(restaurantId: string): Promise<AddOnResponse[]> {
    const raw = await apiFetchData<Array<Record<string, unknown>>>(
      `/api/public/restaurants/${restaurantId}/menu/addons`,
      { method: 'GET' }
    );
    return (raw ?? []).map(mapAddOnFromBackend);
  },

  async getAddOnsByCategory(restaurantId: string, categoryId: string): Promise<AddOnResponse[]> {
    const raw = await apiFetchData<Array<Record<string, unknown>>>(
      `/api/public/restaurants/${restaurantId}/menu/addons/category/${categoryId}`,
      { method: 'GET' }
    );
    return (raw ?? []).map(mapAddOnFromBackend);
  },

  async getAddOnsForItemCategory(restaurantId: string, categoryId: string): Promise<AddOnResponse[]> {
    const raw = await apiFetchData<Array<Record<string, unknown>>>(
      `/api/public/restaurants/${restaurantId}/menu/addons/for-item-category/${categoryId}`,
      { method: 'GET' }
    );
    return (raw ?? []).map(mapAddOnFromBackend);
  },

  async getAddOnsGroupedByCategory(restaurantId: string): Promise<Record<string, AddOnResponse[]>> {
    const raw = await apiFetchData<Record<string, Array<Record<string, unknown>>>>(
      `/api/public/restaurants/${restaurantId}/menu/addons/grouped-by-category`,
      { method: 'GET' }
    );
    if (!raw || typeof raw !== 'object') return {};
    const out: Record<string, AddOnResponse[]> = {};
    for (const [key, arr] of Object.entries(raw)) {
      out[key] = (arr ?? []).map(mapAddOnFromBackend);
    }
    return out;
  },

  async getAddOnCategories(restaurantId: string): Promise<AddOnCategoryResponse[]> {
    const raw = await apiFetchData<Array<Record<string, unknown>>>(
      `/api/public/restaurants/${restaurantId}/menu/addon-categories`,
      { method: 'GET' }
    );
    return (raw ?? []).map(mapAddOnCategoryFromBackend);
  },

  async getAddOnCategoryById(restaurantId: string, categoryId: string): Promise<AddOnCategoryResponse> {
    const raw = await apiFetchData<Record<string, unknown>>(
      `/api/public/restaurants/${restaurantId}/menu/addon-categories/${categoryId}`,
      { method: 'GET' }
    );
    return mapAddOnCategoryFromBackend(raw ?? {});
  },

  // ==================== LEGACY ALIASES ====================

  /** @deprecated Use getRestaurantById instead */
  async getRestaurant(restaurantId: string): Promise<RestaurantResponse> {
    return this.getRestaurantById(restaurantId);
  },

  // ==================== PUBLIC ORDERS ====================

  async checkDelivery(
    restaurantId: string,
    request: import('./types/checkout').CheckDeliveryRequest
  ): Promise<import('./types/checkout').CheckDeliveryResponse> {
    const body = {
      street: request.street,
      streetNumber: request.streetNumber,
      postalCode: request.postalCode,
      city: request.city,
      ...(request.country != null && request.country !== '' && { country: request.country }),
    };
    const raw = await apiFetchData<Record<string, unknown>>(
      `/api/public/restaurants/${restaurantId}/check-delivery`,
      { method: 'POST', body }
    );
    if (!raw || typeof raw !== 'object') return { available: false };
    const r = raw as Record<string, unknown>;
    const available = r.canDeliver ?? r.available ?? false;
    return {
      available: Boolean(available),
      canDeliver: Boolean(available),
      reason: r.reason as string | null | undefined,
      message: (r.message ?? r.reason) as string | undefined,
      distanceKm: typeof r.distanceKm === 'number' ? r.distanceKm : (r.deliveryDistanceKm as number | undefined),
      deliveryDistanceKm: typeof r.deliveryDistanceKm === 'number' ? r.deliveryDistanceKm : (r.distanceKm as number | undefined),
      maxDeliveryRadiusKm: typeof r.maxDeliveryRadiusKm === 'number' ? r.maxDeliveryRadiusKm : undefined,
      deliveryFee: typeof r.deliveryFee === 'number' ? r.deliveryFee : undefined,
      minOrderAmount: typeof r.minOrderAmount === 'number' ? r.minOrderAmount : undefined,
      deliveryZone: r.deliveryZone as string | undefined,
    } as import('./types/checkout').CheckDeliveryResponse;
  },

  async checkScheduledAvailability(
    restaurantId: string,
    body: import('./types/checkout').ScheduledOrderCheckRequest
  ): Promise<import('./types/checkout').ScheduledOrderCheckResponse> {
    return apiFetchData<import('./types/checkout').ScheduledOrderCheckResponse>(
      `/api/public/restaurants/${restaurantId}/scheduled-orders/check`,
      { method: 'POST', body }
    );
  },

  async getPaymentConfig(restaurantId: string): Promise<{ publishableKey: string }> {
    const raw = await apiFetchData<{ publishableKey: string }>(
      `/api/public/restaurants/${restaurantId}/payments/config`
    );
    return raw ?? { publishableKey: '' };
  },

  async previewOrder(
    restaurantId: string,
    body: import('./types/checkout').OrderPreviewRequest
  ): Promise<import('./types/checkout').OrderPreviewResponse> {
    const raw = await apiFetchData<Record<string, unknown>>(
      `/api/public/restaurants/${restaurantId}/orders/preview`,
      { method: 'POST', body }
    );
    return (raw ?? {}) as unknown as import('./types/checkout').OrderPreviewResponse;
  },

  async createOrder(
    restaurantId: string,
    body: import('./types/checkout').CreateOrderRequest
  ): Promise<import('./types/checkout').OrderResponse> {
    const raw = await apiFetchData<Record<string, unknown>>(
      `/api/public/restaurants/${restaurantId}/orders`,
      { method: 'POST', body }
    );
    return (raw ?? {}) as unknown as import('./types/checkout').OrderResponse;
  },

  async getPublicOrder(orderId: string): Promise<import('./types/checkout').OrderResponse> {
    const raw = await apiFetchData<Record<string, unknown>>(
      `/api/public/orders/${orderId}`
    );
    return (raw ?? {}) as unknown as import('./types/checkout').OrderResponse;
  },

  async getMenuDeals(restaurantId: string, orderType?: string): Promise<DealSectionResponse[]> {
    try {
      const url = orderType
        ? `/api/public/restaurants/${restaurantId}/menu?orderType=${orderType}`
        : `/api/public/restaurants/${restaurantId}/menu`;
      const raw = await apiFetchData<{ deals?: Array<Record<string, unknown>> }>(url);
      if (!Array.isArray(raw?.deals) || raw.deals.length === 0) return [];
      return raw.deals.map(mapDealFromBackend);
    } catch {
      return [];
    }
  },
};
